import React, { useMemo } from 'react'
import { VegaLite } from 'react-vega'
import _cloneDeep from 'lodash/fp/cloneDeep'

import { makeStyles, useTheme, alpha } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import CircularProgress from '@material-ui/core/CircularProgress'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import { Alert, AlertTitle } from '@material-ui/lab'

import {
  useServersQuery,
  useMonitorNetworkQuery,
  useReportQuery,
  useDownloadCsv,
  mergeQueryResultsWith,
} from '../../services/stream-manager'

import useFilterParams from '../../hooks/useFilterParams'

import formatAPIParams from '../../utils/formatAPIParams'


const useCombinedStatus = mergeQueryResultsWith(() => undefined)

const themeGraphSpec = (_spec, theme) => {
  const spec = _cloneDeep(_spec)
  spec.background = theme.palette.background.paper
  spec.config.view.stroke = theme.palette.divider
  spec.width = 'container'
  spec.height = 300
  spec.padding = 20
  spec.autosize = {
    type: 'fit',
    contains: 'padding',
  }
  spec.encoding.color.scale.range = [
    theme.palette.info.main,
  ]
  spec.encoding.y.axis.domainColor = alpha(theme.palette.divider, 0.35)
  spec.encoding.y.axis.titleColor = theme.palette.text.secondary
  spec.encoding.y.axis.labelColor = theme.palette.text.primary
  spec.encoding.y.axis.gridColor = theme.palette.divider
  spec.encoding.y.axis.tickColor = theme.palette.divider
  spec.encoding.x.axis.domainColor = alpha(theme.palette.divider, 0.35)
  spec.encoding.x.axis.titleColor = theme.palette.text.secondary
  spec.encoding.x.axis.labelColor = theme.palette.text.primary
  spec.encoding.x.axis.gridColor = theme.palette.divider
  spec.encoding.x.axis.tickColor = theme.palette.divider
  spec.data.format = {
    parse: { d: "utc:'%Y-%m-%dT%H:%M:%S'" },
  }
  return spec
}

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(1),
    height: '100%'
  },
  title: {
    padding: theme.spacing(1),
  },
  serverSelect: {
    minWidth: '15em',
  },
  chart: {
    width: '100%',
  },
  noData: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    fontSize: theme.typography.subtitle1.fontSize,
    textTransform: 'uppercase',
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
}))

const NetworkHealth = ({ from, until }) => {
  const theme = useTheme()
  const classes = useStyles()
  const [{ server }, setFilterParams] = useFilterParams()

  const serversQuery = useServersQuery()

  const monitorBandwidthQuery = useMonitorNetworkQuery({
    id: server?.id,
    metric: 'bandwidth',
    params: formatAPIParams({ from, until }),
  }, { skip: !server?.id })

  const monitorLatencyQuery = useMonitorNetworkQuery({
    id: server?.id,
    metric: 'latency',
    params: formatAPIParams({ from, until }),
  }, { skip: !server?.id })

  const combinedQueryStatus = useCombinedStatus(monitorBandwidthQuery, monitorLatencyQuery)

  const [downloadBandwidthReport] = useDownloadCsv(useReportQuery)({
    params: formatAPIParams({
      from,
      until,
      topics: ['media_server_bandwidth'],
      target_id: server?.id,
    }),
  }, { skip: !server?.id })

  const [downloadLatencyReport] = useDownloadCsv(useReportQuery)({
    params: formatAPIParams({
      from,
      until,
      topics: ['media_server_latency'],
      target_id: server?.id,
    }),
  }, { skip: !server?.id })

  const bandwidthSpec = useMemo(() => {
    if (monitorBandwidthQuery.data == null) return
    return themeGraphSpec(monitorBandwidthQuery.data.graph_spec, theme)
  }, [theme, monitorBandwidthQuery.data])

  const latencySpec = useMemo(() => {
    if (monitorLatencyQuery.data == null) return
    return themeGraphSpec(monitorLatencyQuery.data.graph_spec, theme)
  }, [theme, monitorLatencyQuery.data])

  return (
    <Paper className={classes.container}>
      <Typography variant="h6" component="p" className={classes.title}>
        Media Server Network Health
      </Typography>
      <FormControl className={classes.formControl} margin="dense">
        <InputLabel id="media-server-select-label" margin="dense">
          Media Server
        </InputLabel>
        {serversQuery.isSuccess && (
          <Select
            className={classes.serverSelect}
            labelId="media-server-select-label"
            id="media-server-select"
            value={server?.id}
            onChange={(e) => setFilterParams({ server: e.target.value || undefined })}
            label="Monitor period"
            margin="dense"
          >
            {serversQuery.data?.map(({ id, name }) => (
              <MenuItem key={id} value={id}>{name}</MenuItem>
            ))}
          </Select>
        )}
      </FormControl>
      {Boolean(server?.id) && (
        <Grid container spacing={0} className={classes.grid}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" component="p" className={classes.title}>
              Bandwidth
            </Typography>
            {monitorBandwidthQuery.isLoading ? (
              <CircularProgress />
            ) : monitorBandwidthQuery.isSuccess ? (
              bandwidthSpec.datasets[bandwidthSpec.data.name]?.length > 0 ? (
                <>
                  <VegaLite className={classes.chart} spec={bandwidthSpec} actions={false} />
                  <Button
                    disabled={!server?.id}
                    color="primary"
                    variant="contained"
                    onClick={downloadBandwidthReport}
                  >
                    Download CSV report
                  </Button>
                </>
              ) : (
                <Typography variant="h6" component="p" className={classes.noData}>
                  No data recorded for the selected period
                </Typography>
              )
            ) : monitorBandwidthQuery.isError ? (
              <Alert severity="error">
                <AlertTitle>{monitorBandwidthQuery.error.data?.status_code}</AlertTitle>
                {combinedQueryStatus.error.data?.detail}
              </Alert>
            ) : null}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" component="p" className={classes.title}>
              Latency
            </Typography>
            {monitorLatencyQuery.isLoading ? (
              <CircularProgress />
            ) : monitorLatencyQuery.isSuccess ? (
              latencySpec.datasets[latencySpec.data.name]?.length > 0 ? (
                <>
                  <VegaLite className={classes.chart} spec={latencySpec} actions={false} />
                  <Button
                    disabled={!server?.id}
                    color="primary"
                    variant="contained"
                    onClick={downloadLatencyReport}
                  >
                    Download CSV report
                  </Button>
                </>
              ) : (
                <Typography variant="h6" component="p" className={classes.noData}>
                  No data recorded for the selected period
                </Typography>
              )
            ) : monitorLatencyQuery.isError ? (
              <Alert severity="error">
                <AlertTitle>{monitorLatencyQuery.error.data?.status_code}</AlertTitle>
                {combinedQueryStatus.error.data?.detail}
              </Alert>
            ) : null}
          </Grid>
        </Grid>
      )}
    </Paper>
  )
}

export default NetworkHealth
